import Vue from "vue";
import * as api from "@/api/redeCredenciada";
import * as geoUtils from "@/utils/geoDistance";
import { allowUpdate } from "@/utils/checkReadonlyUtil";
import { VerificarAps } from "@/api/agenda";
import { compareTextAsc } from "@/utils/sortUtil";
import { capitalize } from "@/utils/utils";

function extractCategoriaRede(category = {}) {
  return category.Rede ?? category.CodigoRede ?? category.codigoRede ?? category.CdRede ?? "";
}

function resolveAffiliateRede(state) {
  if (state.rede) return state.rede;

  const redeFromResultados = state.resultadosBusca?.Data?.Rede;
  if (redeFromResultados) return redeFromResultados;

  const tipoAtendimento = state.tipoAtendimento;
  if (tipoAtendimento && state.homeCategories?.length) {
    const category = state.homeCategories.find(item => (item.Codigo ?? item.TipoView) === tipoAtendimento);
    const redeFromCategory = extractCategoriaRede(category);
    if (redeFromCategory) return redeFromCategory;
  }

  return "";
}

const initialState = {
  groups: [],
  homeCategories: [],
  homeRecommendations: [],
  homeFrequentSearches: [],
  homeFavoritos: [],
  specialities: [],
  subSpecialities: [],
  locations: [],
  regions: {},
  states: [],
  matches: [],
  planos: [],
  plano: "",
  redes: [],
  rede: "",
  prestadoresSendoDescredenciados: [],
  tipoAtendimento: "",
  group: "",
  codigoEspecialidade: "",
  descricaoEspecialidade: "",
  codigoSubEspecialidade: "",
  descricaoSubEspecialidade: "",
  state: "",
  city: "",
  neighborhood: "",
  locationType: "",
  locationName: "",
  docInfo: {},
  match: {},
  favorites: [],
  latestViews: [],
  locationFilter: {},
  deactivated: false,
  recommendationDetail: {},
  fetchAccessResource: false,
  filters: {},
  sort: "distancia",
  isAps: false,
  isRecording: false,
  speechRecognition: null,
  termo: "",
  resultadosBusca: null, // Armazena a resposta completa da nova API obterResultados
  enderecoSelecionado: null, // Armazena o endereço selecionado via ModalSearchAddress
  locationDisabled: false,
  addresses: [],
  paginacao: {
    pagina: 1,
    tamanhoPagina: 20,
    total: 0
  }
};

// vuex module
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  actions: {
    async salvarFavorito(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) return 0;
      if (!allowUpdate()) return 0;

      try {
        const result = await api.SalvarFavorito({
          vinculo: context.rootState.saude.vinculo,
          prestador,
          endereco
        });

        // Atualiza as listas que dependem de favoritos para manter a consistência
        context.dispatch("fetchFavorites");
        context.dispatch("fetchLatestViews");

        return result.Data;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deletarFavorito(context, { prestador }) {
      if (!context.rootState.saude.vinculo) return 0;
      if (!allowUpdate()) return 0;

      try {
        const result = await api.DeletarFavorito({
          vinculo: context.rootState.saude.vinculo,
          codigoPrestador: prestador
        });

        context.dispatch("fetchFavorites");
        return result.Data;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async obterDetalhesPrestador(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) return null;
      try {
        const result = await api.ObterDetalhesPrestador({
          vinculo: context.rootState.saude.vinculo,
          prestador,
          endereco
        });
        return result;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async addFavorite(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) return 0;
      if (!allowUpdate()) return 0;
      const result = await api.AdicionarFavorito({
        vinculo: context.rootState.saude.vinculo,
        prestador,
        endereco
      });
      context.dispatch("fetchFavorites");
      context.dispatch("fetchLatestViews");
      return result.Data;
    },
    async remFavorite(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) return 0;
      if (!allowUpdate()) return 0;
      const result = await api.RemoverFavorito({
        vinculo: context.rootState.saude.vinculo,
        prestador,
        endereco
      });
      context.dispatch("fetchFavorites");
      return result.Data;
    },
    async testFavorite(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) {
        return 0;
      }
      const result = await api.VerificarFavorito({
        vinculo: context.rootState.saude.vinculo,
        prestador,
        endereco
      });
      return result.Data;
    },
    async fetchFavorites(context) {
      if (!context.rootState.saude.vinculo) return 0;
      await context.commit("SET_LAST_RECOMMENDATION_DETAIL", {});
      const prestadores = await api.BuscarFavoritos({
        vinculo: context.rootState.saude.vinculo
      });
      if (prestadores.Data) {
        const data = await geoUtils.CalculateDistance(prestadores.Data, "GmLatitude", "GmLongitude");
        await context.commit("SET_FAVORITES", data);

        return prestadores.Data.length;
      }
      return 0;
    },
    async addAffiliateView(context, { prestador, endereco }) {
      if (!context.rootState.saude.vinculo) return 0;
      if (!allowUpdate()) return 0;
      const result = await api.RegistrarAcesso({
        vinculo: context.rootState.saude.vinculo,
        prestador,
        endereco
      });
      context.dispatch("fetchLatestViews");
      return result.Data;
    },
    async fetchLatestViews(context) {
      if (!context.rootState.saude.vinculo) {
        return 0;
      }
      const prestadores = await api.BuscarHistoricoAcesso({
        vinculo: context.rootState.saude.vinculo
      });
      if (prestadores.Data) {
        const data = await geoUtils.CalculateDistance(prestadores.Data, "GmLatitude", "GmLongitude");
        await context.commit("SET_LAST_VIEWS", data);
        return prestadores.Data.length;
      }
      return 0;
    },
    async fetchLocationFilter(context) {
      const tpR = "Seu endereço residencial";
      const tpC = "Seu local de trabalho";
      const tpG = "Perto de Sua Localização Atual";
      const locationFilter = context.rootState.saude.vinculo
        ? {
            R: context.state.locationFilter.R ?? tpR,
            C: context.state.locationFilter.C ?? tpC,
            G: context.state.locationFilter.G ?? tpG,
            latitude: context.state.locationFilter.latitude ?? 0,
            longitude: context.state.locationFilter.longitude ?? 0
          }
        : {
            G: context.state.locationFilter.G ?? tpG,
            latitude: context.state.locationFilter.latitude ?? 0,
            longitude: context.state.locationFilter.longitude ?? 0
          };
      const buscas = [];
      if (context.rootState.saude.vinculo && (!locationFilter.R || locationFilter.R === tpR)) {
        buscas.push(
          api
            .BuscarEnderecoReferencia({
              vinculo: context.rootState.saude.vinculo,
              tipoEndereco: "R",
              latitude: 0,
              longitude: 0
            })
            .then(locationR => {
              if (locationR?.Data) {
                locationFilter.R = locationR.Data;
              }
            })
        );
      }
      if (context.rootState.saude.vinculo && (!locationFilter.C || locationFilter.C === tpC)) {
        buscas.push(
          api
            .BuscarEnderecoReferencia({
              vinculo: context.rootState.saude.vinculo,
              tipoEndereco: "C",
              latitude: 0,
              longitude: 0
            })
            .then(locationC => {
              if (locationC?.Data) {
                locationFilter.C = locationC.Data;
              }
            })
        );
      }
      const coords = await geoUtils.GetLocation().catch(() => null);
      if (context.rootState.saude.vinculo && coords) {
        if (coords.latitude !== locationFilter.latitude || coords.longitude !== locationFilter.longitude) {
          buscas.push(
            api
              .BuscarEnderecoReferencia({
                vinculo: context.rootState.saude.vinculo,
                tipoEndereco: "",
                latitude: coords.latitude,
                longitude: coords.longitude
              })
              .then(locationG => {
                if (locationG?.Data) {
                  locationFilter.G = locationG.Data;
                  locationFilter.latitude = coords.latitude;
                  locationFilter.longitude = coords.longitude;
                }
              })
          );
        }
      }
      if (buscas?.length) {
        await Promise.all(buscas);
      }
      await context.commit("SET_LOCATION_FILTER", locationFilter);
    },
    async fetchPlans(context) {
      if (context.state.planos?.length) return;
      const planos = await api.ObterPlanos();
      if (planos.Data) {
        await context.commit("SET_PLANOS", planos.Data);
      }
    },
    async fetchRedes(context) {
      if (context.state.redes?.length) return;
      const redes = await api.ObterRedes();
      if (redes.Data) {
        await context.commit("SET_REDES", redes.Data);
      }
    },
    async fetchGroups(context) {
      const response = await api.buscaDeRedeHome({ vinculo: context.rootState.saude.vinculo });
      const data = response?.Data ?? {};

      const favoritosRaw = Array.isArray(data.Favoritos) ? data.Favoritos : [];
      const favoritos = favoritosRaw.map(p => ({
        CodigoPrestador: p.CodigoPrestador ?? p.codigoPrestador ?? "",
        SrEndereco: p.CodigoEndereco ?? p.SrEndereco ?? p.codigoEndereco ?? "",
        DescricaoPrestador: p.Nome ?? p.DescricaoPrestador ?? p.nome ?? "",
        DescricaoEspecialidade: p.Especialidade ?? p.DescricaoEspecialidade ?? p.especialidade ?? "",
        EnderecoRua: p.Endereco ?? p.EnderecoRua ?? p.endereco ?? "",
        EnderecoNumero: p.Numero ?? p.EnderecoNumero ?? p.numero ?? "",
        EnderecoComplemento: p.Complemento ?? p.EnderecoComplemento ?? p.complemento ?? "",
        EnderecoBairro: p.Bairro ?? p.EnderecoBairro ?? p.bairro ?? "",
        EnderecoCidade: p.Cidade ?? p.EnderecoCidade ?? p.cidade ?? "",
        CodigoEstado: p.Uf ?? p.CodigoEstado ?? p.uf ?? "",
        Distancia: p.DistanciaKm ?? p.Distancia ?? p.distanciaKm ?? 0,
        Teleconsulta: p.Telemedicina ?? p.Teleconsulta ?? p.PossuiTeleconferencia ?? false,
        TpRede: p.TipoRede ?? p.TpRede ?? p.Rede ?? "",
        NetworkName: p.NomeRede ?? p.NetworkName ?? p.nomeRede ?? "",
        Favorito: p.Favorito ?? false,
        WhatsApp: p.PossuiWhatsapp ?? p.WhatsApp ?? false,
        Agendamento: p.PossuiAgendaOnline ?? p.Agendamento ?? false,
        ScoreTotal: p.Avaliacao ?? p.ScoreTotal ?? 0,
        DsIdioma: p.DsIdioma ?? ""
      }));

      const categorias = (data.Categorias ?? []).map(category => ({
        ...category,
        Rede: extractCategoriaRede(category)
      }));

      context.commit("SET_GROUPS", categorias);
      context.commit("SET_HOME_CATEGORIES", categorias);
      context.commit("SET_HOME_RECOMMENDATIONS", data.Recomendacoes ?? []);
      context.commit("SET_HOME_FREQUENT_SEARCHES", data.BuscasFrequentes ?? []);
      context.commit("SET_HOME_FAVORITOS", favoritos);
    },
    async saveSearchHistory({ rootState }, termo) {
      if (!termo || !termo.trim()) return;
      return api.GuardarBusca({ vinculo: rootState.saude.vinculo, termo: termo.trim() });
    },
    async requestUserLocation({ commit, state, rootState }) {
      const location = await geoUtils.GetLocation().catch(() => null);
      commit("SET_LOCATION_DISABLED", !location);

      if (location) {
        try {
          const refResponse = await api.BuscarEnderecoReferencia({
            vinculo: rootState.saude.vinculo || "",
            tipoEndereco: "",
            latitude: location.latitude,
            longitude: location.longitude
          });

          if (refResponse && refResponse.Data) {
            // Formatando a string do endereço: Rua na primeira linha, o resto na segunda
            const partesEndereco = refResponse.Data.split("-");

            const rua = partesEndereco[0].split(",")[0] || "";
            const numero = partesEndereco[0].split(",")[1] || "";
            const bairro = partesEndereco[1] ? partesEndereco[1].trim() : "";
            const cidade = partesEndereco[2] ? partesEndereco[2].trim().split("-")[0] : "";
            const estado = partesEndereco[3] ? partesEndereco[3].trim() : "";

            const enderecoPrincipal = `${rua}${numero ? `, ${numero.trim()}` : ""}`;
            const enderecoSecundario = [bairro, cidade, estado].filter(Boolean).join(", ");
            const descricaoFormatada = `${enderecoPrincipal} - ${enderecoSecundario}`;

            // Atualiza o endereço "atual" na lista de endereços para exibir a rua/bairro formatado
            const updatedAddresses = state.addresses.map(addr => {
              if (addr.Tipo === "atual" || addr.Tipo === "Atual") {
                return {
                  ...addr,
                  Titulo: "Usar minha localização atual",
                  Descricao: descricaoFormatada,
                  Latitude: location.latitude,
                  Longitude: location.longitude
                };
              }
              return addr;
            });
            commit("SET_ADDRESSES", updatedAddresses);

            // Atualiza também o endereço selecionado, caso seja o "atual"
            if (
              state.enderecoSelecionado &&
              (state.enderecoSelecionado.Tipo === "atual" || state.enderecoSelecionado.Tipo === "Atual")
            ) {
              commit("SET_ENDERECO_SELECIONADO", {
                ...state.enderecoSelecionado,
                Titulo: enderecoPrincipal || "Localização",
                Descricao: descricaoFormatada,
                Latitude: location.latitude,
                Longitude: location.longitude
              });
            }
          }
        } catch (error) {
          console.error("Erro ao buscar endereço da localização atual:", error);
        }
      }

      return location;
    },

    async getAddressSearch({ rootState, commit }) {
      const response = await api.obterEnderecosBusca({ vinculo: rootState.saude.vinculo });
      const enderecos = response?.Data?.Enderecos ?? [];

      // Garante que o mapeamento dos novos campos da API fique padronizado no store
      const mappedEnderecos = enderecos.map(end => ({
        ...end,
        Titulo: end.Tipo === "outra-cidade" ? "Buscar outro endereço" : end.Titulo,
        CodigoEstado: end.Estado || "",
        NomeEstado: end.Estado || "",
        CodigoCidade: end.Cidade || "",
        NomeCidade: end.Cidade || "",
        Bairro: end.Bairro || "",
        Latitude: end.Latitude || 0,
        Longitude: end.Longitude || 0,
        EnderecoFormatado:
          `${end.Rua || ""}${end.Numero ? ` ${end.Numero}` : ""} - ${capitalize(end.Bairro) || ""}, ${capitalize(
            end.Cidade
          )}/${end.Estado}` || ""
      }));

      commit("SET_ADDRESSES", mappedEnderecos);

      const selectedAddress = mappedEnderecos.find(endereco => endereco.Selecionado);
      if (selectedAddress) {
        commit("SET_ENDERECO_SELECIONADO", selectedAddress);
      }

      return response;
    },
    async fetchSpecialities(context, codigoCategoria = context.state.tipoAtendimento) {
      context.commit("SET_SPECIALITIES", []);
      const specs = await api.ObterEspecialidades({
        vinculo: context.rootState.saude.vinculo,
        codigoCategoria
      });

      const specialities = specs?.Data?.Especialidades ?? specs?.Data ?? [];
      if (specialities?.length) {
        const normalized = specialities.map(spec => ({
          CodigoEspecialidade: spec.Codigo ?? spec.CodigoEspecialidade,
          DescricaoEspecialidade: spec.Titulo ?? spec.DescricaoEspecialidade,
          Ordem: spec.Ordem
        }));
        context.commit("SET_SPECIALITIES", normalized);
      } else {
        throw new Error("Nenhuma especialidade encontrada");
      }
    },
    async fetchSubSpecialities(context) {
      context.commit("SET_SUB_SPECIALITIES", []);
      const { rede, tipoAtendimento } = context.state;
      const specs = await api.ObterSubEspecialidades({
        vinculo: context.rootState.saude.vinculo,
        rede,
        tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade,
        inativos: context.state.deactivated
      });
      context.commit("SET_SUB_SPECIALITIES", specs.Data ?? []);
    },
    async fetchCitiesNetwork(context, { estado }) {
      const locs = await api.BuscarCidade({
        vinculo: context.rootState.saude.vinculo,
        estado,
        // Estados/cidades dependem da categoria + especialidade escolhidas pelo usuário.
        tipoAtendimento: context.state.tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade
      });

      if (locs && locs.Data) {
        await context.commit("SET_CITIES", locs.Data);
        return locs.Data;
      }
      return [];
    },
    async fetchStatesNetwork(context) {
      const locs = await api.BuscarEstados({
        vinculo: context.rootState.saude.vinculo,
        tipoAtendimento: context.state.tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade
      });

      if (locs && locs.Data) {
        await context.commit("SET_STATES", locs.Data);
        return locs.Data;
      }
      return [];
    },
    async fetchRegions(context) {
      const locs = await api.ObterRegioes({
        vinculo: context.rootState.saude.vinculo,
        rede: context.state.rede,
        tipoAtendimento: context.state.tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade,
        subEspecialidade: context.state.codigoSubEspecialidade,
        inativos: context.state.deactivated
      });

      if (locs.Data) {
        await context.commit("SET_REGIONS", locs.Data.Regioes);
        const states = Object.keys(locs.Data.Regioes).sort(compareTextAsc);
        await context.commit("SET_STATES", states);
      } else {
        throw new Error("nenhuma região encontrada");
      }
    },
    async checkDeactivateMatch(context) {
      if (context.state.deactivatedMatches && !context.state.match.CodigoPrestador) {
        await context.commit("SET_LOCATION_TYPE", context.state.deactivatedMatches.locationType);
        await context.commit("SET_MATCH", context.state.deactivatedMatches.match);
        await context.commit("SET_MATCHES", context.state.deactivatedMatches.matches);
        await context.commit("SET_DEACTIVATED", context.state.deactivatedMatches.deactivated);
        await context.commit("SET_DEACTIVATED_MATCHES", null);
      }
    },
    async fetchDoctorsInfo(context, { rede, codigoPrestador, srEndereco, inativos }) {
      if (
        context.state.docInfo?.CodigoPrestador === codigoPrestador &&
        context.state.docInfo?.SrEndereco === srEndereco
      ) {
        return;
      }
      if (context.state.deactivated !== inativos) {
        context.dispatch("SET_DEACTIVATED", inativos);
      }
      const completo =
        context.state.match?.CodigoPrestador !== codigoPrestador || context.state.match?.SrEndereco !== srEndereco;
      const info = await api.DetalhePrestador({
        vinculo: context.rootState.saude.vinculo,
        rede,
        codigoPrestador,
        srEndereco,
        inativos,
        completo
      });
      const docInfo = info?.Data ?? {};
      docInfo.CodigoPrestador = codigoPrestador;
      docInfo.SrEndereco = srEndereco;
      if (completo && docInfo.Prestador) {
        let matches = [docInfo.Prestador];
        matches = await geoUtils.CalculateDistance(matches, "GmLatitude", "GmLongitude");
        context.commit("SET_MATCH", matches[0]);
      }

      context.commit("SET_DOCINFO", docInfo);
    },
    async fetchSubstitutes(context) {
      let substitutos = await api.ObterPrestadoresSubstitutos({
        vinculo: context.rootState.saude.vinculo,
        rede: context.state.rede,
        codigoPrestador: context.state.match.CodigoPrestador,
        srEndereco: context.state.match.SrEndereco
      });
      context.commit("SET_DEACTIVATED_MATCHES", {
        locationType: context.state.locationType,
        matches: context.state.matches,
        match: context.state.match,
        deactivated: context.state.deactivated
      });
      const data = await geoUtils.CalculateDistance(substitutos?.Data ?? [], "GmLatitude", "GmLongitude");
      context.commit("SET_MATCHES", data);
      context.commit("SET_LOCATION_TYPE", "Credenciados substitutos");
      context.commit("SET_DEACTIVATED", false);
    },
    async fetchMatchesByProximity(context, type) {
      if (type !== "C" && type !== "R") {
        throw new Error("Wrong value for type param at fetchRegionsByProximity");
      }
      const docs = await api.BuscarPorProximidade({
        vinculo: context.rootState.saude.vinculo,
        rede: context.state.rede,
        tipoAtendimento: context.state.tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade,
        subEspecialidade: context.state.codigoSubEspecialidade,
        inativos: context.state.deactivated,
        buscaProximidadeTipo: type
      });

      if (docs.Data) {
        await context.commit("SET_LOCATION_NAME", docs.Data.EnderecoReferencia);
        const data = docs.Data.PrestadorProximidade.map(({ Prestador, Distancia, Favorito }) => ({
          ...Prestador,
          Distancia,
          Favorito
        }));
        await context.commit("SET_MATCHES", data);
      } else {
        await context.commit("SET_LOCATION_NAME", "");
        throw new Error(docs.message ? docs.message : "Nenhum resultado encontrato");
      }
    },
    async fetchMatchesByText(context, nome) {
      const response = await context.dispatch("searchProviders", {
        termo: nome,
        usarLocalizacaoAtual: false
      });
      const total = response?.Data?.Prestadores?.length ?? 0;
      if (total > 0) {
        return Promise.resolve(total);
      }
      return Promise.reject(new Error("nenhum resultado encontrato"));
    },
    async fetchMatchesNear(context) {
      const coords = await geoUtils.GetLocation().catch(() => null);
      if (!coords) {
        throw new Error("nenhum resultado encontrato");
      }
      const docs = await api.BuscarPelaProximidadeAtual({
        vinculo: context.rootState.saude.vinculo,
        rede: context.state.rede,
        tipoAtendimento: context.state.tipoAtendimento,
        codigoEspecialidade: context.state.codigoEspecialidade,
        subEspecialidade: context.state.codigoSubEspecialidade,
        latitude: coords.latitude,
        longitude: coords.longitude,
        inativos: false
      });

      if (docs.Data) {
        await context.commit("SET_LOCATION_NAME", docs.Data.EnderecoReferencia);
        const data = docs.Data.PrestadorProximidade.map(({ Prestador, Distancia, Favorito }) => ({
          ...Prestador,
          Distancia,
          Favorito
        }));
        await context.commit("SET_MATCHES", data);
      } else {
        await context.commit("SET_LOCATION_NAME", "");
        throw new Error("nenhum resultado encontrato");
      }
    },
    async fetchPlansAttended(context) {
      const docs = await api.ObterPlanosAtendidos({
        vinculo: context.rootState.saude.vinculo,
        rede: context.state.rede,
        codigoPrestador: context.state.match.CodigoPrestador
      });

      return docs?.Data ?? [];
    },
    async fetchPrestadorPlans(context, { prestador, rede }) {
      const vinculo = context.rootState.saude.vinculo || "";
      const result = await api.ObterPlanosPrestador({
        vinculo,
        prestador,
        rede
      });

      return result?.Data ?? [];
    },
    async hasAllowedAffiliateIndication(context) {
      if (!context.rootState.saude.vinculo) {
        return false;
      }
      if (context.state.allowAffiliateIndication) {
        return true;
      }
      const allowAffiliateIndication = await context.dispatch(
        "myAccount/fetchAccessResource",
        { funcionalidade: 1, permissao: 3 },
        { root: true }
      );
      await context.commit("SET_ALLOW_AFFILIATE_INDICATION", allowAffiliateIndication);
      return allowAffiliateIndication;
    },
    async reset(context) {
      context.commit("RESET");
      if (context.state.groups.length === 0) {
        await context.dispatch("fetchGroups");
      }
      context.dispatch("hasAllowedAffiliateIndication");
    },
    async fetchStates(context) {
      let states = [];
      context.state.states.forEach(element => states.push({ value: element, label: element }));
      return states;
    },
    async fetchCities(context) {
      try {
        const cs = Object.keys(context.state.regions[context.state.state]).sort(compareTextAsc);
        let cities = [];
        cs.forEach(element => cities.push({ value: element, label: element }));
        return cities;
      } catch {
        return [];
      }
    },
    async fetchNeighborhoods(context) {
      try {
        const ns = context.state.regions[context.state.state][context.state.city].sort(compareTextAsc);
        let cache = {};
        // retira Ids duplicados
        const rns = ns.filter(e => {
          if (cache[e]) {
            return false;
          }
          cache[e] = true;
          return true;
        });
        let neighborhoods = [];
        rns.forEach(element => neighborhoods.push({ value: element, label: element }));
        return neighborhoods;
      } catch {
        return [];
      }
    },
    async fetchRecommendationRequests(context) {
      const response = await api.PedidosIndicacaoRede({
        vinculo: context.rootState.saude.vinculo
      });

      return !response?.Data || response instanceof Error ? [] : response.Data;
    },
    async fetchPrestadoresSendoDescredenciados(context) {
      const response = await api.ObterNovosAvisosDescredenciamento({
        vinculo: context.rootState.saude.vinculo
      });
      if (response?.Data?.length) {
        context.commit(
          "SET_PRESTADORES_SENDO_DESCREDENCIADOS",
          response.Data.map(a => ({
            codigoRede: a.CodigoRede,
            codigoPrestadorDescredenciado: a.CodigoPrestadorDescredenciado,
            descricaoPrestadorDescredenciado: a.DescricaoPrestadorDescredenciado,
            srEnderecoDescredenciado: a.SrEnderecoDescredenciado,
            enderecoPrestadorDescredenciado: a.EnderecoPrestadorDescredenciado,
            dataDescredenciamento: a.DataDescredenciamento,
            motivoDescredenciamento: a.MotivoDescredenciamento,
            contatoDescredenciamento: a.ContatoDescredenciamento,
            emailDescredenciamento: a.EmailDescredenciamento,
            nomeAssociado: a.NomeAssociado,
            listado: false,
            codigoPrestadorIndicado: a.CodigoPrestadorIndicado,
            descricaoPrestadorIndicado: a.DescricaoPrestadorIndicado,
            srEnderecoIndicado: a.SrEnderecoIndicado,
            enderecoPrestadorIndicado: a.EnderecoPrestadorIndicado,
            telefonePrestadorIndicado: a.TelefonePrestadorIndicado,
            dataIndicado: a.DataIndicado
          }))
        );
      } else {
        context.commit("SET_PRESTADORES_SENDO_DESCREDENCIADOS", []);
      }
    },
    async registrarCienteAvisoDescredenciamento(context, { codigoPrestador, srEndereco }) {
      const prestadoresSendoDescredenciados =
        context.state.prestadoresSendoDescredenciados.map(a =>
          a.codigoPrestadorDescredenciado === codigoPrestador && a.srEnderecoDescredenciado === srEndereco
            ? { ...a, listado: true }
            : { ...a }
        ) ?? [];
      context.commit("SET_PRESTADORES_SENDO_DESCREDENCIADOS", prestadoresSendoDescredenciados);
      await api.RegistrarCienteAvisoDescredenciamento({
        vinculo: context.rootState.saude.vinculo,
        prestador: codigoPrestador,
        endereco: srEndereco
      });
    },
    async fetchRecommendationDetail(context, { ticket, force }) {
      if (!force && `${ticket}` === `${context.state.recommendationDetail.Ticket}`) {
        return context.state.recommendationDetail;
      }
      const coords = await geoUtils.GetLocation().catch(() => null);
      const response = await api.ResultadoIndicacaoRede({
        vinculo: context.rootState.saude.vinculo,
        chamado: ticket,
        latitude: coords === null ? null : coords.latitude,
        longitude: coords === null ? null : coords.longitude
      });
      const recommendationDetail = !response?.Data || response instanceof Error ? {} : response.Data;
      if (recommendationDetail.SeuPedido?.Protocolo) {
        recommendationDetail.Ticket = ticket;
      }
      await context.commit("SET_LAST_RECOMMENDATION_DETAIL", recommendationDetail);
      return recommendationDetail;
    },
    async fetchVerificaAps(context) {
      const isAps = await VerificarAps({
        vinculo: context.rootState.saude.vinculo,
        associado: context.rootState.saude.vinculo
      });
      if (isAps instanceof Error) {
        await context.commit("SET_ISAPS", false);
        return;
      }
      await context.commit("SET_ISAPS", isAps.Data);
    },
    startRecording(context, { initialText = "", onUpdate, onError, onEnd, onUnsupported } = {}) {
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          if (onUnsupported) {
            onUnsupported();
            return;
          }
          alert("Seu navegador não suporta reconhecimento de voz.");
          return;
        }

        if (context.state.isRecording) return;

        const speechRecognition = new SpeechRecognition();
        speechRecognition.lang = "pt-BR";
        speechRecognition.interimResults = true;
        speechRecognition.maxAlternatives = 1;
        context.commit("SET_IS_RECORDING", true);
        context.commit("SET_SPEECH_RECOGNITION", speechRecognition);

        let finalTranscript = initialText || "";

        speechRecognition.onresult = event => {
          let interimTranscript = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += (finalTranscript ? " " : "") + transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          const nextText = finalTranscript + (interimTranscript ? ` ${interimTranscript}` : "");
          if (onUpdate) {
            onUpdate(nextText);
          }
        };

        speechRecognition.onerror = event => {
          context.commit("SET_IS_RECORDING", false);
          context.commit("SET_SPEECH_RECOGNITION", null);
          if (onError) {
            onError(event);
          }
        };

        speechRecognition.onend = () => {
          context.commit("SET_IS_RECORDING", false);
          context.commit("SET_SPEECH_RECOGNITION", null);
          if (onEnd) {
            onEnd();
          }
        };

        speechRecognition.start();
      } catch (err) {
        console.error(err);
        if (onError) {
          onError(err);
          return;
        }
        alert(err.message || "Falha ao iniciar reconhecimento de voz.");
        context.commit("SET_IS_RECORDING", false);
        context.commit("SET_SPEECH_RECOGNITION", null);
      }
    },
    stopRecording(context) {
      try {
        if (!context.state.isRecording) return;
        if (context.state.speechRecognition) {
          context.state.speechRecognition.stop();
          return;
        }
        context.commit("SET_IS_RECORDING", false);
        context.commit("SET_SPEECH_RECOGNITION", null);
      } catch (err) {
        console.error(err);
        context.commit("SET_IS_RECORDING", false);
        context.commit("SET_SPEECH_RECOGNITION", null);
      }
    },
    async searchProviders(context, params = {}) {
      try {
        if (params.termo !== undefined) {
          context.commit("SET_TERMO", params.termo || "");
        }

        const enderecoSec = context.state.enderecoSelecionado || {};
        const locationDisabled = context.state.locationDisabled;

        let coords = null;
        let usarLocalizacaoAtual = params.usarLocalizacaoAtual ?? false;
        let codigoEstado = null;
        let cidade = null;
        let bairro = null;
        let latitude = null;
        let longitude = null;

        if (enderecoSec) {
          if (enderecoSec.Tipo === "atual" || enderecoSec.Tipo === "Atual") {
            if (!locationDisabled) {
              usarLocalizacaoAtual = true;
            }
            // Reusa coords já gravadas no endereço atual (evita GetLocation a cada busca)
            if (enderecoSec.Latitude && enderecoSec.Longitude) {
              latitude = enderecoSec.Latitude;
              longitude = enderecoSec.Longitude;
            }
          } else if (enderecoSec.Tipo === "outra-cidade" || enderecoSec.Tipo === "outra-cidade-selecionada") {
            codigoEstado = enderecoSec.CodigoEstado || null;
            cidade = enderecoSec.NomeCidade || enderecoSec.CodigoCidade || null;
          } else {
            codigoEstado = enderecoSec.Estado || enderecoSec.CodigoEstado || enderecoSec.Uf || null;
            cidade = enderecoSec.Cidade || enderecoSec.CodigoCidade || null;
            bairro = enderecoSec.Bairro || null;
            if (enderecoSec.Latitude && enderecoSec.Longitude) {
              latitude = enderecoSec.Latitude;
              longitude = enderecoSec.Longitude;
            }
          }
        }

        if (usarLocalizacaoAtual && !latitude) {
          coords = await geoUtils.GetLocation().catch(() => null);
        }

        const resolvedLatitude = latitude || (usarLocalizacaoAtual ? coords?.latitude : null);
        const resolvedLongitude = longitude || (usarLocalizacaoAtual ? coords?.longitude : null);
        const missingCoordinates = resolvedLatitude === null || resolvedLatitude === undefined;

        const resolvedRede = resolveAffiliateRede(context.state);
        if (resolvedRede && !context.state.rede) {
          context.commit("SET_REDE", resolvedRede);
        }

        const finalParams = {
          ...params,
          vinculo: context.rootState.saude.vinculo,
          rede: resolvedRede || context.state.rede,
          tipoAtendimento: context.state.tipoAtendimento,
          codigoEspecialidade: context.state.codigoEspecialidade,
          codigoSubEspecialidade: params.codigoSubEspecialidade ?? context.state.codigoSubEspecialidade ?? null,
          codigoEstado: codigoEstado || params.codigoEstado || null,
          cidade: cidade || params.cidade || null,
          bairro: bairro || params.bairro || null,
          logradouro: enderecoSec.Endereco || null,
          numero: enderecoSec.Numero || null,
          inativos: context.state.deactivated,
          // Se já temos coordenadas, não força GetLocation de novo em obterResultados
          usarLocalizacaoAtual: usarLocalizacaoAtual && missingCoordinates,
          latitude: resolvedLatitude,
          longitude: resolvedLongitude,
          ordenacao: params.ordenacao || context.state.sort || "Distancia",
          telemedicina: params.telemedicina ?? null,
          termo: params.termo !== undefined ? params.termo : context.state.termo || null,
          pagina: context.state.paginacao.pagina || 1,
          tamanhoPagina: context.state.paginacao.tamanhoPagina || 20
        };
        const vinculo = context.rootState.saude.vinculo;
        const response = await api.obterResultados(finalParams, vinculo);

        if (response && response.Data) {
          if (!context.state.rede && response.Data.Rede) {
            context.commit("SET_REDE", response.Data.Rede);
          }

          context.commit("SET_RESULTADOS_BUSCA", response);

          // Mapeamento da nova API para a interface legada
          if (response.Data.Prestadores && response.Data.Prestadores.length > 0) {
            const adaptedMatches = response.Data.Prestadores.map(p => ({
              CodigoPrestador: p.CodigoPrestador,
              SrEndereco: p.CodigoEndereco,
              DescricaoPrestador: p.Nome,
              DescricaoEspecialidade: p.Especialidade,
              EnderecoRua: capitalize(p.Endereco ?? p.EnderecoRua ?? ""),
              EnderecoNumero: p.Numero ?? p.EnderecoNumero ?? "",
              EnderecoComplemento: p.Complemento ?? p.EnderecoComplemento ?? "",
              EnderecoBairro: capitalize(p.Bairro ?? p.EnderecoBairro ?? ""),
              EnderecoCidade: capitalize(p.Cidade ?? p.EnderecoCidade ?? ""),
              CodigoEstado: p.Uf,
              Distancia: p.DistanciaKm,
              Teleconsulta: p.Telemedicina ?? p.PossuiTeleconferencia ?? false,
              TpRede: p.TipoRede ?? p.TpRede ?? "",
              NetworkName: p.NomeRede ?? "",
              Favorito: p.Favorito,
              ScoreTotal: p.Avaliacao ?? p.ScoreTotal ?? 0,
              Idiomas: p.Idiomas ?? [],
              DsIdioma: ((p.Idiomas ?? []).join(",") || p.DsIdioma) ?? "",
              WhatsApp: p.PossuiWhatsapp ?? p.WhatsApp ?? false,
              Agendamento: p.PossuiAgendaOnline ?? p.Agendamento ?? false,
              SubEspecialidades: p.SubEspecialidade || "",
              // Códigos de subespecialidade preservados para o filtro de tela (client-side no doctors-list).
              SubEspecialidadesCodigos: Array.isArray(p.SubEspecialidades)
                ? p.SubEspecialidades.map(se => se && se.Codigo).filter(Boolean)
                : []
            }));

            if (params.append) {
              context.commit("SET_MATCHES", [...context.state.matches, ...adaptedMatches]);
            } else {
              context.commit("SET_MATCHES", adaptedMatches);
            }
          } else {
            if (!params.append) {
              context.commit("SET_MATCHES", []);
            }
          }
        } else {
          if (!params.append) {
            context.commit("SET_MATCHES", []);
          }
        }
        return response;
      } catch (error) {
        if (!params.append) {
          context.commit("SET_MATCHES", []);
        }
        return Promise.reject(error);
      }
    }
  },
  mutations: {
    SET_LOCATION_DISABLED: (state, value) => {
      Vue.set(state, "locationDisabled", value);
    },
    LOGOUT_USER: state => Object.keys(state).forEach(p => Vue.set(state, p, initialState[p])),
    RESET_CONTEXT: state => Object.keys(state).forEach(p => Vue.set(state, p, initialState[p])),
    SET_LAST_RECOMMENDATION_DETAIL: (state, recommendationDetail) => {
      Vue.set(state, "recommendationDetail", recommendationDetail);
    },
    SET_GROUPS: (state, nextGroups) => {
      Vue.set(state, "groups", nextGroups);
    },
    SET_HOME_CATEGORIES: (state, categories) => {
      Vue.set(state, "homeCategories", categories);
    },
    SET_HOME_RECOMMENDATIONS: (state, recommendations) => {
      Vue.set(state, "homeRecommendations", recommendations);
    },
    SET_HOME_FREQUENT_SEARCHES: (state, searches) => {
      Vue.set(state, "homeFrequentSearches", searches);
    },
    SET_HOME_FAVORITOS: (state, favoritos) => {
      Vue.set(state, "homeFavoritos", favoritos);
    },
    SET_FILTERS: (state, filters) => {
      Vue.set(state, "filters", filters);
    },
    SET_SORT: (state, sort) => {
      Vue.set(state, "sort", sort);
    },
    SET_SPECIALITIES: (state, nextSpecs) => {
      Vue.set(state, "specialities", nextSpecs);
    },
    SET_SUB_SPECIALITIES: (state, nextSpecs) => {
      Vue.set(state, "subSpecialities", nextSpecs);
    },
    SET_CITIES: (state, nextCities) => {
      Vue.set(state, "cities", nextCities);
    },
    SET_REDE: (state, nextRede) => {
      Vue.set(state, "rede", nextRede);
    },
    SET_TIPO_ATENDIMENTO: (state, nextTipo) => {
      Vue.set(state, "tipoAtendimento", nextTipo);
    },
    SET_GROUP: (state, nextGroup) => {
      Vue.set(state, "group", nextGroup);
    },
    SET_SPECIALITY: (state, { cod, label }) => {
      Vue.set(state, "codigoEspecialidade", cod);
      Vue.set(state, "descricaoEspecialidade", label);
    },
    SET_SUB_SPECIALITY: (state, { cod, label }) => {
      Vue.set(state, "codigoSubEspecialidade", cod);
      Vue.set(state, "descricaoSubEspecialidade", label);
    },
    SET_REGIONS: (state, nextRegions) => {
      Vue.set(state, "regions", nextRegions);
    },
    SET_STATE: (state, nextState) => {
      Vue.set(state, "state", nextState);
    },
    SET_STATES: (state, states) => {
      Vue.set(state, "states", states);
    },
    SET_CITY: (state, nextCity) => {
      Vue.set(state, "city", nextCity);
    },
    SET_NEIGHBORHOOD: (state, nextNeighbor) => {
      Vue.set(state, "neighborhood", nextNeighbor);
    },
    SET_MATCHES: (state, nextMatches) => {
      Vue.set(state, "matches", nextMatches);
    },
    SET_DEACTIVATED_MATCHES: (state, nextMatches) => {
      Vue.set(state, "deactivatedMatches", nextMatches);
    },
    SET_LOCATION_TYPE: (state, nextType) => {
      Vue.set(state, "locationType", nextType);
    },
    SET_LOCATION_NAME: (state, nextLocationName) => {
      Vue.set(state, "locationName", nextLocationName);
    },
    SET_DOCINFO: (state, docInfo) => {
      Vue.set(state, "docInfo", docInfo);
    },
    SET_MATCH: (state, nextMatch) => {
      Vue.set(state, "match", nextMatch);
    },
    SET_LAST_VIEWS: (state, latestViews) => {
      Vue.set(state, "latestViews", latestViews);
    },
    SET_FAVORITES: (state, favorites) => {
      Vue.set(state, "favorites", favorites);
    },
    SET_LOCATION_FILTER: (state, locationFilter) => {
      Vue.set(state, "locationFilter", locationFilter);
    },
    SET_DEACTIVATED: (state, deactivated) => {
      Vue.set(state, "deactivated", deactivated);
      Vue.set(state, "title", deactivated ? "Prestadores inativos" : "Busca de rede");
    },
    SET_ALLOW_AFFILIATE_INDICATION: (state, allowAffiliateIndication) => {
      Vue.set(state, "allowAffiliateIndication", allowAffiliateIndication);
    },
    SET_REDES: (state, redes) => {
      Vue.set(state, "redes", redes);
    },
    SET_PLANOS: (state, planos) => {
      Vue.set(state, "planos", planos);
    },
    SET_PLANO: (state, plano) => {
      Vue.set(state, "plano", plano);
    },
    SET_PRESTADORES_SENDO_DESCREDENCIADOS: (state, prestadoresSendoDescredenciados) =>
      Vue.set(state, "prestadoresSendoDescredenciados", prestadoresSendoDescredenciados),
    SET_ISAPS: (state, value) => {
      Vue.set(state, "isAps", value);
    },
    SET_IS_RECORDING: (state, value) => {
      Vue.set(state, "isRecording", value);
    },
    SET_SPEECH_RECOGNITION: (state, value) => {
      Vue.set(state, "speechRecognition", value);
    },
    SET_TERMO: (state, value) => {
      Vue.set(state, "termo", value);
    },
    SET_RESULTADOS_BUSCA: (state, payload) => {
      Vue.set(state, "resultadosBusca", payload);
      if (payload && payload.Data) {
        Vue.set(state.paginacao, "total", payload.Data.Total || 0);
        Vue.set(state.paginacao, "pagina", payload.Data.Pagina || 1);
        Vue.set(state.paginacao, "tamanhoPagina", payload.Data.TamanhoPagina || 20);
      }
    },
    SET_PAGINACAO: (state, payload) => {
      state.paginacao = { ...state.paginacao, ...payload };
    },
    SET_ADDRESSES: (state, payload) => {
      Vue.set(state, "addresses", payload ?? []);
    },
    SET_ENDERECO_SELECIONADO: (state, payload) => {
      Vue.set(state, "enderecoSelecionado", payload);
    },
    RESET: state => {
      Vue.set(state, "specialities", []);
      Vue.set(state, "subSpecialities", []);
      Vue.set(state, "locations", []);
      Vue.set(state, "regions", {});
      Vue.set(state, "states", []);
      Vue.set(state, "matches", []);
      Vue.set(state, "rede", "");
      Vue.set(state, "tipoAtendimento", "");
      Vue.set(state, "group", "");
      Vue.set(state, "codigoEspecialidade", "");
      Vue.set(state, "descricaoEspecialidade", "");
      Vue.set(state, "codigoSubEspecialidade", "");
      Vue.set(state, "descricaoSubEspecialidade", "");
      Vue.set(state, "state", "");
      Vue.set(state, "city", "");
      Vue.set(state, "neighborhood", "");
      Vue.set(state, "locationType", "");
      Vue.set(state, "locationName", "");
      Vue.set(state, "docInfo", {});
      Vue.set(state, "match", {});
      Vue.set(state, "deactivatedMatches", null);
      Vue.set(state, "filters", {});
      Vue.set(state, "sort", "Distancia");
      Vue.set(state, "isAps", false);
      Vue.set(state, "isRecording", false);
      Vue.set(state, "speechRecognition", null);
      Vue.set(state, "termo", "");
    }
  }
};
